import axios, { AxiosRequestConfig, CancelTokenSource } from 'axios';
import { PlanningAlertForm } from '../planning/planningTypes';

const fetchAll = (type:string) => ({ type: `FETCH_${type.toUpperCase()}_REQUEST` });
const fetchAllSuccess = (type: string, results: any) => ({ type: `FETCH_${type.toUpperCase()}_SUCCESS`, [type]: results });
const fetchAllError = (type: string, error: any, response?: any) => ({ type: `FETCH_${type.toUpperCase()}_FAILURE`, error, response });
export const fetchAllAsync = (controller: string, type: string, cancelTokenSource: CancelTokenSource | null) => {
    cancelTokenSource && cancelTokenSource.cancel('Cancelling already running request');
    return (dispatch: (arg0: { [x: string]: any; type: string; error?: any; }) => void) => {
        dispatch(fetchAll(type));
        return axios.get(`/api/${controller}`, {
            cancelToken: (cancelTokenSource = axios.CancelToken.source()).token
        }).then(response => {
            cancelTokenSource = null;
            dispatch(fetchAllSuccess(type, response.data));
        }).catch(error => {
            cancelTokenSource = null;
            if (axios.isCancel(error)) {
                console.log('cancel', error);
            } else {
                dispatch(fetchAllError(type, error.id, error.response));
                console.error(error);
            }
        });
    };
};

const fetch = (type: string, id: number | string) => ({ type: `FETCH_${type.toUpperCase()}_REQUEST`, id });
const fetchSuccess = (type: string, id: number | string, result: any) => ({ type: `FETCH_${type.toUpperCase()}_SUCCESS`, id, [type]: result });
const fetchError = (type: string, id: number | string, error: any) => ({ type: `FETCH_${type.toUpperCase()}_FAILURE`, id, error });
export const fetchAsync = (controller: string, type: string, id: string | number, cancelTokenSource: CancelTokenSource | null) => {
    cancelTokenSource && cancelTokenSource.cancel('Cancelling already running request');
    return (dispatch: (arg0: { [x: string]: any; type: string; id: string | number; error?: any; }) => void) => {
        dispatch(fetch(type, id));
        return axios.get(`/api/${controller}/${id}`, {
            cancelToken: (cancelTokenSource = axios.CancelToken.source()).token
        }).then(response => {
            cancelTokenSource = null;
            dispatch(fetchSuccess(type, id, response.data));
        }).catch(error => {
            cancelTokenSource = null;
            if (axios.isCancel(error)) {
                console.log('cancel', error);
            } else {
                dispatch(fetchError(type, id, error.response));
                console.error(error);
            }
        });
    };
};

const create = (type: string) => ({ type: `CREATE_${type.toUpperCase()}_REQUEST` });
const createSuccess = (type: string, result: any) => ({ type: `CREATE_${type.toUpperCase()}_SUCCESS`, [type]: result });
const createError = (type: string, error: any) => ({ type: `CREATE_${type.toUpperCase()}_FAILURE`, error });
export const createAsync = (controller: string, type: string, params: any, cancelTokenSource: CancelTokenSource | null) => {
    cancelTokenSource && cancelTokenSource.cancel('Cancelling already running request');
    return (dispatch: (arg0: { [x: string]: any; type: string; error?: any; }) => void) => {
        dispatch(create(type));
        return axios.post(`/api/${controller}`, {
            cancelToken: (cancelTokenSource = axios.CancelToken.source()).token,
            ...params
        }).then(response => {
            cancelTokenSource = null;
            dispatch(createSuccess(type, response.data));
        }).catch(error => {
            cancelTokenSource = null;
            if (axios.isCancel(error)) {
                console.log('cancel', error);
            } else {
                dispatch(createError(type, error.response));
                console.error(error);
            }
        });
    };
};

const deleteRequest = (type: string, id: number | string) => ({ type: `DELETE_${type.toUpperCase()}_REQUEST`, id });
const deleteSuccess = (type: string, id: number | string, response?: any) => ({ type: `DELETE_${type.toUpperCase()}_SUCCESS`, id, response });
const deleteError = (type: string, id: number | string, error: any) => ({ type: `DELETE_${type.toUpperCase()}_FAILURE`, id, error });
export const deleteAsync = (controller: string, type: string, id: string | number, cancelTokenSource: CancelTokenSource | null) => {
    cancelTokenSource && cancelTokenSource.cancel('Cancelling already running request');
    return (dispatch: (arg0: { type: string; id: string | number; error?: any; }) => void) => {
        dispatch(deleteRequest(type, id));
        return axios.delete(`/api/${controller}/${id}`, {
            cancelToken: (cancelTokenSource = axios.CancelToken.source()).token
        }).then(response => {
            cancelTokenSource = null;
            dispatch(deleteSuccess(type, id, response.data));
        }).catch(error => {
            cancelTokenSource = null;
            if (axios.isCancel(error)) {
                console.log('cancel', error);
            } else {
                dispatch(deleteError(type, id, error.response));
                console.error(error);
            }
        });
    };
};

const renameRequest = (type: string, id: number | string) => ({ type: `RENAME_${type.toUpperCase()}_REQUEST`, id });
const renameSuccess = (type: string, id: number | string, name: string) => ({ type: `RENAME_${type.toUpperCase()}_SUCCESS`, id, name });
const renameError = (type: string, id: number | string, error: any) => ({ type: `RENAME_${type.toUpperCase()}_FAILURE`, id, error });
export const renameAsync = (controller: string, type: string, id: string | number, name: string, cancelTokenSource: CancelTokenSource | null) => {
    cancelTokenSource && cancelTokenSource.cancel('Cancelling already running request');
    return (dispatch: (arg0: { type: string; id: string | number; name?: string; error?: any; }) => void) => {
        dispatch(renameRequest(type, id));
        return axios.patch(`/api/${controller}/${id}/rename`, {
            cancelToken: (cancelTokenSource = axios.CancelToken.source()).token,
            params: { new_name: name }
        }).then(response => {
            cancelTokenSource = null;
            dispatch(renameSuccess(type, id, name));
        }).catch(error => {
            cancelTokenSource = null;
            if (axios.isCancel(error)) {
                console.log('cancel', error);
            } else {
                dispatch(renameError(type, id, error.response));
                console.error(error);
            }
        });
    };
};

// Template for running any action - needs reviewing
const actionRequest = (type: string, id: number | string) => ({ type: `${type.toUpperCase()}_REQUEST`, id });
const actionSuccess = (type: string, key: string, id: number | string, result?: undefined) => ({ type: `${type.toUpperCase()}_SUCCESS`, id, [key]: result });
const actionError = (type: string, id: number | string, error: any) => ({ type: `${type.toUpperCase()}_FAILURE`, id, error });
export const actionAsync = (controller: any, type: string, key: any, id: string, action: any, params: any, cancelTokenSource: CancelTokenSource | null) => {
    cancelTokenSource && cancelTokenSource.cancel('Cancelling already running request');
    return (dispatch: (arg0: { [x: string]: string | number | undefined; type: string; id: string | number; error?: any; }) => void) => {
        dispatch(actionRequest(type, id));
        return axios.patch(`/api/${controller}/${id}/${action}`, {
            ...params,
            cancelToken: (cancelTokenSource = axios.CancelToken.source()).token
        }).then(response => {
            cancelTokenSource = null;
            dispatch(actionSuccess(type, id, response.data));
        }).catch(error => {
            cancelTokenSource = null;
            if (axios.isCancel(error)) {
                console.log('cancel', error);
            } else {
                dispatch(actionError(type, id, error.response));
                console.error(error);
            }
        });
    };
};

// Generic by passing in actions and it just runs it - needs reviewing
export const axiosAsync = (id: any, axiosRequestConfig: AxiosRequestConfig, requestAction: (arg0: any) => any, successAction: (arg0: any, arg1: any) => any, failureAction: (arg0: any, arg1: any) => any, cancelTokenSource: CancelTokenSource | null) => {
    cancelTokenSource && cancelTokenSource.cancel('Cancelling already running request');
    return (dispatch: (arg0: any) => void) => {
        dispatch(requestAction(id));
        return axios({
            ...axiosRequestConfig,
            cancelToken: (cancelTokenSource = axios.CancelToken.source()).token
        }).then(response => {
            cancelTokenSource = null;
            dispatch(successAction(id, response.data));
        }).catch(error => {
            cancelTokenSource = null;
            if (axios.isCancel(error)) {
                console.log('cancel', error);
            } else {
                dispatch(failureAction(id, error.response));
                console.error(error);
            }
        });
    };
};

export const commonDeleteAsync = (id: number, route: string, requestDispatch: { (id: number): { type: string; id: number; }; (arg0: any): any; }, successDispatch: { (id: number, planningAlert: PlanningAlertForm): { type: string; id: number; planningAlert: PlanningAlertForm; }; (arg0: any, arg1: any): any; }, errorDispatch: { (id: number, error: any): { type: string; id: number; error: any; }; (arg0: any, arg1: any): any; }) => {
    return (dispatch: (arg0: { type: string; id: number; error?: any; }) => void) => {
        dispatch(requestDispatch(id));
        return axios.delete(`${route}/${id}`, {
            // cancelToken: (deletePlanningAlertSource = axios.CancelToken.source()).token
        }).then(response => {
            dispatch(successDispatch(id, response.data));
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log('cancel');
            } else {
                dispatch(errorDispatch(id, error.response));
                console.error(error);
            }
        });
    };
};